import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, lifecycle, withProps } from 'recompose';
import { Page, Container } from 'components/core/Page';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';
import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';
import { withRouteHandler, withErrorHandler } from 'decorators';
import { createBooking } from 'store/modules/bookings';
import unitSelector from 'store/selectors/unit';
import { change as changeFormData } from 'redux-form';
import { phonePropType } from 'config/propTypes';
import { H2, Text, Paragraph } from 'components/core/Typography';
import classnames from 'classnames';
import { Image } from 'components/core/Image';
import { Notification } from 'components/core/Notification';
import { FacilityDetails } from 'components/organisms/FacilityPage/FacilityDetails/FacilityDetails';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import plusIcon from 'assets/images/map/Plus.svg';
import minusIcon from 'assets/images/map/Minus.svg';
import { getScarcityFlag } from 'utils/store/getScarcityFlag';
import formatUnit from 'utils/store/formatUnit';
import { formatUnitSize } from 'utils/unitNameTrim';
import UnitIconV2 from 'components/unit/UnitIconV2/UnitIconV2';
import { Card } from 'components/core/Card';
import checkImage from 'assets/images/confirmation/checkout-page-check.svg';
import { formatUSD } from '@sparefoot/react-core/utils/format';
import UnitReservationFormV2 from 'components/unit/UnitReservationFormV2/UnitReservationFormV2';
import './UnitPageV2.scss';

export const enhance = compose(
	connect(
		unitSelector,
		{ createBooking, changeFormData },
		mapDispatchToActions,
	),
	withErrorHandler,
	withRouteHandler((props) => !props.unit),
	lifecycle({
		componentDidMount() {
			this.props.actions.changeFormData(
				'unit-reservation',
				'moveInDate',
				null,
			);
		},
	}),
	withPageViewTracking(PAGE_TYPES.RESERVE),
	withProps(({ unit }) => ({
		scrollToReviews: () => scrollToElement('#facility-page-user-reviews'),
		unit: unit ? formatUnit(unit) : unit,
		isScarce: getScarcityFlag([unit]),
	})),
);

export function UnitPageV2({
	facility,
	outsideReservationWindow,
	phone,
	unit,
	trackingPixel,
	meta,
	reviews,
	scrollToReviews,
	actions,
	isScarce,
}) {
	const [isCollapsed, setIsCollapsed] = useState(true);

	const tierAndType = unit.tierName
		? `${unit.tierName} ${unit.typeText}`
		: unit.typeText;

	const toggleCollapse = () => {
		setIsCollapsed(!isCollapsed);
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			toggleCollapse();
		}
	};

	return (
		<Page
			className="unit-page-v2"
			trackingPixel={trackingPixel}
			pageType={PAGE_TYPES.RESERVE}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
			noIndex
		>
			{outsideReservationWindow && (
				<Notification>
					Sorry, this facility only allows reservations for this unit
					for {unit.reservationWindow} days. Select another unit,
					facility, or an earlier move-in date.
				</Notification>
			)}
			<Container>
				<div className="facility-details-container">
					<FacilityDetails
						facility={facility}
						reviews={reviews}
						numberOfRatings={facility.reviews?.number}
						phone={phone}
						scrollToReviews={scrollToReviews}
					/>
				</div>
				<div className="unit-reservation-page__container">
					<div className="unit-reservation-page__left">
						<Card className="unit-summary-card">
							<div className="unit-reservation-page__mobile-header">
								<div
									className="unit-reservation-page__mobile-expand"
									onClick={toggleCollapse}
									onKeyDown={handleKeyDown}
									role="button"
									tabIndex={0}
								>
									<p className="collapse-expand-btn">
										{isCollapsed ? 'EXPAND' : 'COLLAPSE'}
									</p>
									<span className="unit-reservation-page__expand-collapse-icon">
										<Image
											src={
												isCollapsed
													? plusIcon
													: minusIcon
											}
											alt={
												isCollapsed
													? 'Expand'
													: 'Collapse'
											}
											className={
												isCollapsed
													? 'expand'
													: 'collapse'
											}
										/>
									</span>
								</div>
								<div
									className={classnames(
										'unit-reservation-page__unit-info',
										{
											collapsed: !isCollapsed,
											expanded: isCollapsed,
										},
									)}
								>
									<div className="unit-reservation-page__unit-header-info">
										<div className="unit-reservation-page__unit-header-text">
											<Text
												className="unit-size-label-description"
												size="medium"
											>
												{formatUnitSize(unit.sizeText)}{' '}
												{tierAndType}{' '}
											</Text>
											{isScarce && (
												<Text
													className="scarcity-message"
													last
												>
													ONLY {unit?.quantity} LEFT!
												</Text>
											)}
										</div>
										{unit?.special && (
											<Paragraph
												size="small"
												className="unit-reservation-page__promotion"
												weight="ultraBold"
												family="standard"
											>
												{unit?.special}
											</Paragraph>
										)}
									</div>
									<div className="unit-reservation-page__mobile-price">
										<H2
											size="standard"
											weight="ultraBold"
											family="standard"
											color="black"
										>
											{formatUSD(
												unit.price.sparefoot ||
													unit.price.regular,
											)}
										</H2>
									</div>
								</div>
							</div>
							<div
								className={classnames(
									'unit-reservation-page__selected-unit-card',
									{
										collapsed: isCollapsed,
										expanded: !isCollapsed,
									},
								)}
							>
								<div className="unit-reservation-page__unit-header">
									<UnitIconV2
										squareFootage={unit?.squareFootage}
										type={unit?.unitType}
										amenities={unit?.amenities}
										alt={`Depiction of a ${unit.size} self-storage unit`}
										lazyLoad={false}
									/>
									<div className="unit-reservation-page__unit-header-info">
										<div className="unit-reservation-page__unit-header-text">
											<Text className="unit-size-label-description">
												{formatUnitSize(unit.sizeText)}{' '}
												{tierAndType}{' '}
											</Text>
											{isScarce && (
												<Text
													className="scarcity-message"
													last
												>
													ONLY {unit.quantity} LEFT!
												</Text>
											)}
										</div>
										{unit?.special && (
											<Paragraph
												size="small"
												className="unit-reservation-page__promotion"
												weight="ultraBold"
											>
												{unit?.special}
											</Paragraph>
										)}
									</div>
								</div>
								<Card className="unit-reservation-page__card features-card">
									<p className="amenities-heading">
										Features
									</p>
									<ul className="amenities">
										{unit?.availableAmenities?.map(
											(amenity, index, array) => (
												<React.Fragment key={amenity}>
													<li
														key={amenity}
														className="amenities-item"
													>
														{amenity}
													</li>

													{index <
														array.length - 1 && (
														<span className="amenities-elipses-dot"></span>
													)}
												</React.Fragment>
											),
										)}
									</ul>
								</Card>
								<Card className="unit-reservation-page__card pricing-card">
									<div className="unit-reservation-page__pricing-header">
										<p className="monthly-unit-rent">
											Monthly Unit Rent
										</p>
										<p className="monthly-unit-rent-price">
											{formatUSD(
												unit?.price?.sparefoot ||
													unit?.price?.regular,
											)}
										</p>
									</div>
									{facility?.totalFees > 0 && (
										<>
											<div className="unit-reservation-page__admin-fees">
												<div className="unit-reservation-page__pricing-header">
													<p className="admin-fees-label">
														Admin Fees
													</p>
													<p className="admin-fees-value">
														{formatUSD(
															facility?.totalFees,
														)}
													</p>
												</div>
												<div className="unit-reservation-page__pricing">
													<span className="unit-reservation-page__pricing_label">
														The following fees shall
														apply:
													</span>
													<ul className="unit-reservation-page__pricing_items">
														{facility?.adminFee >
															0 && (
															<li>
																One-Time Admin
																Fee: $
																{
																	facility?.adminFee
																}
															</li>
														)}
														{facility?.lockFee >
															0 && (
															<li>
																<Text
																	color="black"
																	size="smaller"
																>
																	One-Time
																	Lock Fee: $
																	{
																		facility?.lockFee
																	}
																</Text>
															</li>
														)}
													</ul>
												</div>
											</div>
											<Paragraph
												color="gray"
												weight="standard"
												className="unit-reservation-page__admin-fee-description"
											>
												The one-time, non-refundable
												admin fee is charged upon
												signing your lease agreement.
												This fee covers the costs
												associated with processing your
												application and setting up your
												account and access credentials,
												ensuring a smooth move-in
												process.
											</Paragraph>
										</>
									)}
								</Card>
								<div className="unit-reservation-page__move-in">
									<span>Payment due at move-in</span>
								</div>
							</div>
						</Card>
					</div>
					<div className="unit-reservation-page__right">
						<Card className="reservation-form-card">
							<div className="header-container">
								<H2
									size="medium"
									className="checkoutpage-reservation-header"
								>
									Reserve today to lock in this rate now
								</H2>
								<div className="check-with-text-container">
									<div className="check-with-text">
										<span
											className="check-with-text__icon"
											aria-hidden
										>
											<Image
												src={checkImage}
												alt="check"
											/>
										</span>
										<span className="check-with-text__label">
											Risk free
										</span>
									</div>
									<div className="check-with-text">
										<span
											className="check-with-text__icon"
											aria-hidden
										>
											<Image
												src={checkImage}
												alt="check"
											/>
										</span>
										<span className="check-with-text__label">
											Cancel anytime
										</span>
									</div>
									<div className="check-with-text">
										<span
											className="check-with-text__icon"
											aria-hidden
										>
											<Image
												src={checkImage}
												alt="check"
											/>
										</span>
										<span className="check-with-text__label">
											No payment required
										</span>
									</div>
								</div>
							</div>
							<UnitReservationFormV2
								onSubmit={actions.createBooking}
								unit={unit}
								facility={facility}
							/>
						</Card>
					</div>
				</div>
			</Container>
		</Page>
	);
}

UnitPageV2.propTypes = {
	facility: PropTypes.object,
	unit: PropTypes.object,
	outsideReservationWindow: PropTypes.bool,
	phone: phonePropType,
	trackingPixel: PropTypes.string.isRequired,
	meta: PropTypes.object,
	reviews: PropTypes.arrayOf(PropTypes.object),
	scrollToReviews: PropTypes.func,
	actions: PropTypes.shape({
		createBooking: PropTypes.func,
		changeFormData: PropTypes.func,
	}),
	isScarce: PropTypes.bool,
};

export default enhance(UnitPageV2);
